/**
 * ============================================================================
 * TEMPLATES STORE - DELETE OPERATIONS
 * ============================================================================
 * 
 * Handles deleting templates via the backend API.
 * 
 * Features:
 * - Existence check before deletion
 * - Graceful handling of already-deleted items
 * - Sync issue recovery (removes from store if API says not found)
 * 
 * @module templates/api/delete
 */

import { apiService } from "@/lib/api-functions/api-service";
import { templatesStore } from "../store";
import type { ToastFunction } from "../types";

// ============================================================================
// DELETE OPERATIONS
// ============================================================================

/**
 * Delete a template via API and sync the store.
 * 
 * Features:
 * - Checks if template exists before attempting deletion
 * - Handles already-deleted templates gracefully
 * - Recovers from sync issues (template in store but not in DB)
 * 
 * @param id - The template ID to delete
 * @param gameId - The game ID
 * @param toast - Optional toast function for notifications
 * 
 * @example
 * ```ts
 * await deleteTemplate('template-123', 'game-456', toast);
 * ```
 */
export async function deleteTemplate(
  id: string,
  gameId: string,
  toast?: ToastFunction
): Promise<void> {
  try {
    // ======================================================================
    // CHECK IF TEMPLATE EXISTS
    // ======================================================================
    const storeData = templatesStore.getData();
    const templateExists = storeData.find(bp => bp.id === id && bp.game_id === gameId);

    if (!templateExists) {
      console.warn('[deleteTemplate] Template not found in store, may be already deleted:', { id, gameId });
      if (toast) {
        toast({
          title: "Info",
          description: "Template was already deleted or doesn't exist",
        });
      }
      return;
    }
    
    // Validate authentication
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // ======================================================================
    // DELETE VIA API
    // ======================================================================
    const result = await apiService.deleteTemplate(id, gameId);
    
    if (!result.success) {
      // Handle case where template is in store but not in database
      if (result.error?.includes('not found')) {
        console.warn('[deleteTemplate] Template not in DB but in store, removing from store');
        const currentData = templatesStore.getData();
        const newData = currentData.filter(bp => bp.id !== id);
        templatesStore.setData(newData);
        
        if (toast) {
          toast({
            title: "Success",
            description: "Template removed (was already deleted from database)",
          });
        }
        return;
      }
      
      throw new Error(result.error || 'Failed to delete template');
    }

    // ======================================================================
    // UPDATE LOCAL STORE
    // ======================================================================
    const currentData = templatesStore.getData();
    const newData = currentData.filter(bp => bp.id !== id);
    templatesStore.setData(newData);
    
    console.log(`[deleteTemplate] Deleted template: ${id}`);

    if (toast) {
      toast({
        title: "Success",
        description: result.data?.message || "Template deleted successfully",
      });
    }
    
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to delete template';
    console.error('[deleteTemplate] Error:', err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}
